/**
 * Render Jobs — frontend client for the external render worker.
 *
 * The app never renders MP4s itself. It enqueues a row in
 * `render_jobs` containing the full SimulationContent payload and
 * the template key. The Node worker (see /render-worker) polls
 * `status = 'queued'`, claims the row, runs Remotion, uploads to
 * the `renders` Storage bucket, and writes back render_path /
 * thumbnail_path / status.
 *
 * The UI subscribes to `render_jobs` realtime updates to show
 * progress, then signs URLs from the bucket for playback/download.
 */

import { supabase } from "@/integrations/supabase/client";
import { generateSimulationContent, type ContentFormat, type SimulationContent } from "./contentGenerator";

export type RenderStatus =
  | "queued"
  | "preparing"
  | "rendering"
  | "encoding"
  | "uploading"
  | "completed"
  | "failed"
  | "cancelled";

export type TemplateKey = "spiral" | "replay" | "pov" | "progression" | "resonant";

export interface RenderJob {
  id: string;
  asset_id: string;
  template: string;
  status: RenderStatus;
  progress: number;
  render_path: string | null;
  thumbnail_path: string | null;
  render_url: string | null;
  thumbnail_url: string | null;
  duration_frames: number | null;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  worker_id: string | null;
  attempts: number;
  error_log: string | null;
  payload: any;
  metadata: any;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface AssetRowLike {
  id: string;
  scenario_id: string;
  format: string;
}

function templateForFormat(format: string): TemplateKey {
  if (format === "replay") return "replay";
  if (format === "pov") return "pov";
  if (format === "progression") return "progression";
  if (format === "resonant") return "resonant";
  return "spiral";
}

/**
 * Build the SimulationContent payload from an asset row and
 * insert a queued render_jobs row. Returns the new job.
 */
export async function enqueueRender(
  asset: AssetRowLike,
  opts: { template?: TemplateKey; sim?: SimulationContent } = {}
): Promise<{ ok: true; job: RenderJob } | { ok: false; error: string }> {
  const template = opts.template ?? templateForFormat(asset.format);

  let sim: SimulationContent;
  try {
    sim =
      opts.sim ??
      generateSimulationContent({
        format: asset.format as ContentFormat,
        scenarioId: asset.scenario_id,
      });
  } catch (e: any) {
    return { ok: false, error: `Payload build failed: ${e?.message ?? e}` };
  }

  const { data, error } = await (supabase as any)
    .from("render_jobs")
    .insert({
      asset_id: asset.id,
      template,
      status: "queued",
      progress: 0,
      payload: { sim, template, asset_id: asset.id },
      metadata: { format: asset.format, scenario_id: asset.scenario_id },
    })
    .select("*")
    .single();

  if (error) return { ok: false, error: error.message };

  // Mirror onto content_assets so the queue can show "rendering" immediately
  await (supabase as any)
    .from("content_assets")
    .update({
      status: "rendering",
      render_job_id: (data as RenderJob).id,
      render_progress: 0,
      render_error: null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", asset.id);

  return { ok: true, job: data as RenderJob };
}

/** Re-queue a failed job — preserves payload, resets state. */
export async function retryRender(jobId: string): Promise<{ ok: boolean; error?: string }> {
  const { error } = await (supabase as any)
    .from("render_jobs")
    .update({
      status: "queued",
      progress: 0,
      error_log: null,
      worker_id: null,
      started_at: null,
      completed_at: null,
    })
    .eq("id", jobId);
  return error ? { ok: false, error: error.message } : { ok: true };
}

export async function fetchLatestJobForAsset(assetId: string): Promise<RenderJob | null> {
  const { data } = await (supabase as any)
    .from("render_jobs")
    .select("*")
    .eq("asset_id", assetId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return (data as RenderJob | null) ?? null;
}

export async function fetchRecentJobs(limit = 100): Promise<RenderJob[]> {
  const { data } = await (supabase as any)
    .from("render_jobs")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  return ((data as RenderJob[]) ?? []);
}

/**
 * Sign a Storage path in the `renders` bucket for inline playback
 * or download. Returns null if the path is missing or signing fails.
 */
export async function signRenderUrl(path: string | null | undefined, expiresInSec = 3600): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage.from("renders").createSignedUrl(path, expiresInSec);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export const RENDER_STATUS_LABEL: Record<RenderStatus, string> = {
  queued: "Queued",
  preparing: "Preparing",
  rendering: "Rendering",
  encoding: "Encoding",
  uploading: "Uploading",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

export function isTerminal(status: RenderStatus | string | null | undefined): boolean {
  return status === "completed" || status === "failed" || status === "cancelled";
}
